import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { KorviuButtonComponent } from '../../../../shared/components/korviu-button/korviu-button.component';
import { KorviuLoaderComponent } from '../../../../shared/components/korviu-loader/korviu-loader.component';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { IAddReferralResponse, IReferral } from 'core/types/referral.interface';
import { InvitationsService } from 'core';
import { ReferralModalFacade } from '../../services/referral-modal.facade';
import { IUpdateInvitationContactMethodPayload } from 'core/services/invitations.service';
import { UserTypes } from '../../../../shared/types/user.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { EMPTY, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { NetworkError } from '../../../../shared/models/network-error';
import { ErrorCodes } from '../../../../shared/types/error-codes.enum';
import { AddReferralStatuses } from '../core/types/add-referral-statuses.enum';
import { VerifyReferralModalComponent } from '../verify-referral-modal/verify-referral-modal.component';
import { IValidateReferralStatusData } from '../../../../shared/types/validation-status-modal-data.interface';
import { CustomMessageService } from 'core/services/custom-message.service';
import { buildInvitationSendPayload } from 'shared/utils/invitation-send-payload.utils';
import {
  buildWhatsappSharePayload,
  ensureWhatsappShareLogged,
  getWhatsappMessageFromUrl,
  withWhatsappMessage
} from 'shared/utils/whatsapp-share.utils';

const CONTACT_METHOD_VALIDATION_ERROR_CODES = new Set([
  'ERR_EMAIL_REGISTERED',
  'ERR_PHONE_REGISTERED',
  'ERR_DUPLICATE'
]);

@Component({
  selector: 'app-edit-confirmation-modal',
  standalone: true,
  imports: [KorviuButtonComponent, KorviuLoaderComponent, NzIconDirective, TranslateModule, NzModalModule],
  templateUrl: './edit-confirmation-modal.component.html',
  styleUrl: './edit-confirmation-modal.component.scss'
})
export class EditConfirmationModalComponent implements OnInit {
  public isLoading: boolean;

  private _destroyRef = inject(DestroyRef);

  constructor(
    @Inject(NZ_MODAL_DATA) public modalData: { referral: IReferral; userType: UserTypes },
    private _modalRef: NzModalRef,
    private _invitationsService: InvitationsService,
    private _referralModalFacade: ReferralModalFacade,
    private _modalService: NzModalService,
    private _messageService: CustomMessageService,
    private _translateService: TranslateService
  ) {}

  public ngOnInit(): void {}

  public onCancel(): void {
    this._modalRef.destroy();
  }

  public onContinue(): void {
    this.isLoading = true;
    const referral = this.modalData.referral;
    const userType = this.modalData.userType;
    const referralData: any = {
      phone_code: referral?.country_code,
      phone_number: referral?.phone_number
    };
    const contactMethodPayload = this._buildContactMethodPayload(referral);
    const updateContactMethod$ =
      !!referral?.id && !!contactMethodPayload
        ? this._invitationsService.updateInvitationContactMethod(referral.id, contactMethodPayload)
        : of(null);

    updateContactMethod$
      .pipe(
        switchMap(() =>
          this._invitationsService.addReferral(buildInvitationSendPayload(referral, userType), userType, true)
        ),
        switchMap((response: IAddReferralResponse) => {
          const invitationResponse = this._unwrapAddReferralResponse(response);
          referralData.referral_id = invitationResponse?.id;
          referralData.referral_type = invitationResponse?.type;

          return this._shareWhatsappInvitation(invitationResponse, referral.message_template).pipe(
            tap(() => {
              this._handleSuccessfulUpdate(invitationResponse, referralData, userType);
            })
          );
        }),
        tap(() => {
          this._referralModalFacade.referralDetailReloadSubject.next();
        }),
        catchError((error: NetworkError) => {
          if (CONTACT_METHOD_VALIDATION_ERROR_CODES.has(error?.errorPayload?.messageCode)) {
            return of(null);
          }

          if (error?.code === ErrorCodes.ERR_PHONE_EXISTS) {
            this._openVerifyReferralModal(AddReferralStatuses.INVALIDPHONE, referralData, userType);
          } else if (error?.errorPayload?.messageCode === 'ERR_INVITATION_ALREADY_SENT') {
            this._openVerifyReferralModal(AddReferralStatuses.INVITATION_ALREADY_SENT, referralData, userType);
          } else {
            this._openVerifyReferralModal(AddReferralStatuses.ALREADYREGISTERED, referralData, userType);
          }
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _buildContactMethodPayload(referral: IReferral): IUpdateInvitationContactMethodPayload | null {
    const contactType = this._normalizeContactMethodType(referral?.type);

    if (!contactType) {
      return null;
    }

    if (contactType === 'email') {
      return {
        type: 'email',
        email: referral?.email
      };
    }

    return {
      type: contactType,
      phone_number: referral?.phone_number,
      country_code: referral?.country_code
    };
  }

  private _normalizeContactMethodType(type?: string): IUpdateInvitationContactMethodPayload['type'] | null {
    const normalizedType = type?.toLowerCase();

    if (normalizedType === 'email') {
      return 'email';
    }

    if (normalizedType === 'whatsapp') {
      return 'whatsapp';
    }

    if (normalizedType === 'sms' || normalizedType === 'phone') {
      return 'whatsapp';
    }

    return null;
  }

  private _unwrapAddReferralResponse(response: IAddReferralResponse): IAddReferralResponse {
    return response?.data || response;
  }

  private _handleSuccessfulUpdate(response: IAddReferralResponse, referralData: any, userType: UserTypes): void {
    const status = this._getReferralStatus(response);

    if (status === AddReferralStatuses.INVITED) {
      this._messageService.success(this._translateService.instant('buildYourNetwork.invitationUpdated'));
      this._modalRef.destroy(true);
      return;
    }

    this._openVerifyReferralModal(status, referralData, userType);
  }

  private _getReferralStatus(response: IAddReferralResponse): AddReferralStatuses {
    if (response?.approval_required || response?.status?.toLowerCase() === 'pending') {
      return AddReferralStatuses.PENDING_APPROVAL;
    }

    if (response?.whatsapp_url) {
      return AddReferralStatuses.WHATSAPP_READY;
    }

    return AddReferralStatuses.INVITED;
  }

  private _shareWhatsappInvitation(response: IAddReferralResponse, customMessage?: string): Observable<unknown> {
    if (!response?.id || response?.approval_required || !response?.whatsapp_url) {
      return of(null);
    }

    const message = customMessage?.trim() || response.message || getWhatsappMessageFromUrl(response.whatsapp_url);
    if (!message) {
      window.open(response.whatsapp_url, '_blank');
      this._messageService.error(this._translateService.instant('errors.UNKNOWN_ERROR'));
      return EMPTY;
    }

    window.open(withWhatsappMessage(response.whatsapp_url, message), '_blank');

    return this._invitationsService.logWhatsappShare(response.id, buildWhatsappSharePayload(response.id, message)).pipe(
      map(ensureWhatsappShareLogged),
      catchError(() => {
        this._messageService.error(this._translateService.instant('errors.UNKNOWN_ERROR'));
        return EMPTY;
      })
    );
  }

  private _openVerifyReferralModal(status: AddReferralStatuses, referralData: any, userType: UserTypes): void {
    this._modalService.create({
      nzContent: VerifyReferralModalComponent,
      nzData: {
        status,
        referralData,
        userType
      } as IValidateReferralStatusData,
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
    this._modalRef.destroy();
  }
}
